import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import { formatApiError } from "@/lib/api";

const AdminLogin = () => {
  const { user, login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && user.role === "admin") {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#fdfbf7] flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 mb-8"
          data-testid="admin-login-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Mallorca Digital
        </Link>

        <div className="rounded-3xl bg-white border border-neutral-200 p-8 lg:p-10 shadow-[0_18px_40px_rgba(0,0,0,0.06)]">
          <h1 className="font-display text-3xl font-light tracking-tight text-neutral-950">
            {t("admin.login.title")}
          </h1>
          <p className="mt-2 text-sm text-neutral-500">
            {t("admin.login.subtitle")}
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="login-email">{t("admin.login.email")}</Label>
              <Input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-login-email"
                className="rounded-xl h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">{t("admin.login.password")}</Label>
              <Input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-login-password"
                className="rounded-xl h-12"
              />
            </div>

            {error && (
              <div
                className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2"
                data-testid="admin-login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-neutral-950 text-white px-6 py-3.5 text-sm font-medium hover:bg-black disabled:opacity-60 transition-colors"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("admin.login.submit")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
